import { z } from 'zod';

/**
 * Commanded vs actual pump state (greenway_api PR 627). `effectiveStatus` keeps meaning the ACTUAL
 * state (pump-status input); these fields add what the pump was last *told* to do. Every field is
 * optional so an older backend, or a surface the API does not cover, parses unchanged.
 */
export const COMMAND_STATUSES = ['QUEUED', 'EXECUTED', 'FAILED', 'CANCELLED'] as const;

export const pumpStateShape = {
  commandedState: z.enum(['on', 'off']).nullish(),
  actualState: z.enum(['running', 'off']).nullish(),
  hasPumpFeedback: z.boolean().optional(),
  /** False: only `commandedState` is trustworthy — show it marked unconfirmed, not the actual one. */
  stateConfirmed: z.boolean().optional(),
  mismatch: z.object({ active: z.boolean(), since: z.string().nullable() }).nullish(),
};

export const lastCommandSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.enum(COMMAND_STATUSES),
  failureReason: z.string().nullish(),
  at: z.string().nullish(),
});
export type LastCommand = z.infer<typeof lastCommandSchema>;

export type PumpState = {
  commandedState?: 'on' | 'off' | null;
  stateConfirmed?: boolean;
  mismatch?: { active: boolean; since: string | null } | null;
};

export const COMMANDED_LABEL = { on: 'Run', off: 'Off' } as const;

/** No pump feedback and a known commanded state: show the command, marked unconfirmed. */
export function isUnconfirmed(s: PumpState): boolean {
  return s.stateConfirmed === false && Boolean(s.commandedState);
}

export function isMismatch(s: PumpState): boolean {
  return s.mismatch?.active === true;
}

/** `dm_failed` → "the device reported the command as failed" etc.; unknown reasons stay generic. */
export const FAILURE_REASON_COPY: Record<string, string> = {
  dispatch_failed: 'the command could not be sent to the device',
  dm_failed: 'the device could not apply it',
  dm_expired: 'the device did not respond in time',
  dm_cancelled: 'it was cancelled before reaching the device',
};
export function failureReasonText(reason: string | null | undefined): string {
  return (reason && FAILURE_REASON_COPY[reason]) || 'the device did not confirm it';
}

export const PUMP_UNCONFIRMED_LABEL = 'Unconfirmed';
export const PUMP_UNCONFIRMED_HINT =
  'This device does not report pump status, so this is the last command sent.';
export const pumpMismatchCopy = (commanded: 'on' | 'off', since: string | null) =>
  `Pump set to ${commanded === 'on' ? 'Run' : 'Off'} but still ${commanded === 'on' ? 'off' : 'running'} - please check${since ? ` (since ${since})` : ''}.`;
